import {Platform} from 'react-native';
import Config from 'react-native-config';

import {NO_NETWORK_EXCEPTION} from '/enum/errors.enum';
import {
  FETCH_AUTH_TOKEN,
  FETCH_LOGOUT,
  FETCH_MULE_TOKEN,
  FETCH_REFRESH_TOKEN,
  FETCH_RESET_PASSWORD,
  REQUEST_GET,
  REQUEST_POST,
} from '/enum/request.enum';
import {setAutoLoginState} from '/redux/action/biometrics.action';
import {getInfo} from '/redux/action/currentUser.action';
import {
  displayLoginLoading,
  hideLoginLoading,
} from '/redux/action/loading.action';
import {request} from '/redux/api/request';
import {createPromisedAction} from '/util/action.util';
import {createStorage} from '/util/async-storage';
import {
  clearTokenStorage,
  getAccessToken,
  setAccessToken,
  setAccessTokenMule,
  setAutoFillPassword,
  setMuleRefreshToken,
  setRefreshToken,
} from '/util/fetch.util';
import {clearState as clearNavigationState} from '/util/navigation.util';
import {Logger} from '/util/splunk/logger.util';
import {getUserName, setUserNameToken} from '/util/user.util';

import {pushToOfflineQueue} from './network.action';
import {
  getSfmcContactKey,
  getStorageMuleToken,
  revokeMuleToken,
} from './sfmc.action';

interface SignInPayload {
  username: string;
  password: string;
}

interface SignOutPayload {
  username: string;
  token: string;
}

interface ResetPasswordPayload {
  email: string;
}

interface OnBiometricsPayload {
  username: string;
  refresh_token: string;
}

const {setValue: setEmailValue, getValue: getEmailValue} = createStorage({
  key: 'auth-email',
});

export const SET_EMAIL = 'AUTH/SET_EMAIL';
export const SIGN_IN = 'AUTH/SIGN_IN';
export const SIGN_OUT = 'AUTH/SIGN_OUT';
export const READ_EMAIL = 'AUTH/READ_EMAIL';
export const WRITE_EMAIL = 'AUTH/WRITE_EMAIL';
export const REFRESH_TOKEN = 'AUTH/refresh_token';
export const REFRESH_MULETOKEN = 'AUTH/token';

export const RESET_PASSWORD = 'AUTH/RESET_PASSWORD';
const muleHeaderContentType = 'application/json';
const muleHeaderAuthToken = Config.MULE_HEADER_AUTH_TOKEN;
export const signIn = createPromisedAction(
  SIGN_IN,
  ['email', 'password'],
  (dispatchMe: any, inputPayload: SignInPayload, dispatch: any) => {
    const {username, password} = inputPayload;
    const CLIENT_ID = Config.CLIENT_ID;
    const CLIENT_SECRET = Config.CLIENT_SECRET;
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(displayLoginLoading());
    return (
      request(
        FETCH_AUTH_TOKEN,
        {
          method: REQUEST_POST,
          body: {
            username,
            password,
            clientId: CLIENT_ID,
            clientSecret: CLIENT_SECRET,
            grantType: 'password',
            scope: 'openid email offline_access',
          },
        },
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        '',
      )
        .then(res => {
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          const {refreshToken, accessToken, expiresIn} = res;
          if (accessToken && Config.ALLOW_SFMC) {
            getSfmcContactKey({username, password});
          }
          return Promise.all([
            setRefreshToken(refreshToken, 2592000),
            setAutoFillPassword(password, username),
            setUserNameToken(username),
            setAccessToken(accessToken, expiresIn),
          ]);
        })
        .then(dispatchMe)
        .then(() => dispatch(getInfo({trackLoginEvent: true})))
        .then(({error} = {}) => {
          if (error) {
            throw error;
          } else {
            return Promise.resolve();
          }
        })
        .then(() => dispatch(writeEmail({email: username})))
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        .then(() => dispatch(hideLoginLoading()))
        .catch(err => {
          if (err.code === NO_NETWORK_EXCEPTION) {
            dispatch(
              pushToOfflineQueue({
                promisedAction: signIn,
                params: inputPayload,
              }),
            );
          }
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(hideLoginLoading());
          throw err;
        })
    );
  },
);

interface OnBiometricsPayload {
  username: string;
  refresh_token: string; // Specify the type if known
}

export const onBiometrics = createPromisedAction(
  REFRESH_TOKEN,
  [],
  async (dispatchMe: any, inputPayload: OnBiometricsPayload, dispatch: any) => {
    // Add async here
    const {refresh_token} = inputPayload;
    const CLIENT_ID = Config.CLIENT_ID;
    const CLIENT_SECRET = Config.CLIENT_SECRET;
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(displayLoginLoading());
    return (
      request(
        FETCH_REFRESH_TOKEN,
        {
          method: REQUEST_POST,
          body: {
            clientId: CLIENT_ID,
            clientSecret: CLIENT_SECRET,
            grantType: 'refresh_token',
            refreshToken: refresh_token,
            scope: 'openid email offline_access',
          },
        },

        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        '',
      )
        .then(res => {
          // @ts-expect-error TS(2339): Property 'idToken' does not exist on type '{ data:... Remove this comment to see the full error message
          const {refreshToken, accessToken, expiresIn} = res;
          return Promise.all([
            setRefreshToken(refreshToken, 2592000),
            setAccessToken(accessToken, expiresIn),
          ]);
        })
        .then(dispatchMe)
        .then(() => dispatch(getInfo({trackLoginEvent: true})))
        .then(({error} = {}) => {
          if (error) {
            throw error;
          } else {
            return Promise.resolve();
          }
        })
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        .then(() => dispatch(hideLoginLoading()))
        .catch(err => {
          if (err.code === NO_NETWORK_EXCEPTION) {
            dispatch(
              pushToOfflineQueue({
                promisedAction: signIn,
                params: inputPayload,
              }),
            );
          }
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(hideLoginLoading());
          throw err;
        })
    );
  },
);
const headers = {
  'Content-Type': muleHeaderContentType,
  Authorization: muleHeaderAuthToken,
};

export const muleTokenBiometric = createPromisedAction(
  REFRESH_MULETOKEN,
  ['refresh_token'],
  (dispatchMe: any, inputPayload: {refresh_token: string}, dispatch: any) => {
    const {refresh_token} = inputPayload;
    return (
      getStorageMuleToken()
        .then(token => {
          return request(FETCH_MULE_TOKEN, {
            body: {
              access_token: token,
              source: 'COACH_APP',
              refresh_token: refresh_token,
            },
            method: REQUEST_GET,
            muleHeaders: headers,
            isMuleApi: true,
          });
        })
        .then(res => {
          // @ts-expect-error TS(2339): Property 'idToken' does not exist on type '{ data:... Remove this comment to see the full error message
          const {refreshToken, oktaToken, expiresIn} = res;
          return Promise.all([
            setMuleRefreshToken('com.yourApp.refreshMuleToken', refreshToken),
            setAccessTokenMule(oktaToken, expiresIn),
          ]);
        })
        .then(dispatchMe)
        .then(() => dispatch(getInfo({trackLoginEvent: true})))
        .then(({error} = {}) => {
          // Throw new exception if `getInfo` fails
          if (error) {
            throw error;
          } else {
            return Promise.resolve();
          }
        })
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        .then(() => dispatch(hideLoginLoading()))
        .catch(err => {
          if (err.code === NO_NETWORK_EXCEPTION) {
            dispatch(
              pushToOfflineQueue({
                promisedAction: muleTokenBiometric,
                params: inputPayload,
              }),
            );
          }
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(hideLoginLoading());
          throw err;
        })
    );
  },
);
export const handleLogoutCleanup = async (
  dispatch: any,
  dispatchMe: any,
  userName: string,
) => {
  try {
    if (Platform.OS === 'web') {
      window.history.replaceState({}, '', '/');
    }
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    await clearTokenStorage(userName);
    await dispatchMe();
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    await dispatch(setAutoLoginState());
    revokeMuleToken();
    clearNavigationState();
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    await dispatch(hideLoginLoading());
  } catch (err) {
    Logger.manualSplunkLog({
      loginType: 'Logout_Cleanup',
      message: `handle Logout Cleanup error ${err}`,
      severity: 'LOW',
    });
    return err;
  }
};

export const signOut = createPromisedAction(
  SIGN_OUT,
  [],
  async (dispatchMe: any, inputPayload: SignOutPayload, dispatch: any) => {
    const CLIENT_ID = Config.CLIENT_ID;
    const CLIENT_SECRET = Config.CLIENT_SECRET;
    const userName = await getUserName();
    const token = await getAccessToken();
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(displayLoginLoading());

    try {
      await request(
        FETCH_LOGOUT,
        {
          method: REQUEST_POST,
          body: {
            clientId: CLIENT_ID,
            clientSecret: CLIENT_SECRET,
            token,
          },
        },
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        '',
      );
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
      await handleLogoutCleanup(dispatch, dispatchMe, userName);

      const stillHasToken = await getAccessToken();
      if (stillHasToken) {
        await dispatch(getInfo({trackLoginEvent: true}));
      }

      return Promise.resolve();
    } catch (err: any) {
      if (err.code === NO_NETWORK_EXCEPTION) {
        dispatch(
          pushToOfflineQueue({
            promisedAction: signOut,
            params: inputPayload,
          }),
        );
      }
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
      await handleLogoutCleanup(dispatch, dispatchMe, userName);
      throw err;
    }
  },
);

export const onResetPassword = createPromisedAction(
  RESET_PASSWORD,
  [],
  async (
    dispatchMe: any,
    inputPayload: ResetPasswordPayload,
    dispatch: any,
  ) => {
    const {email} = inputPayload;
    return (
      Promise.resolve()
        .then(() =>
          request(FETCH_RESET_PASSWORD, {
            body: {
              emailId: email,
            },
            method: REQUEST_POST,
          }),
        )
        .then(res => {
          dispatch(dispatchMe);
          return res;
        })
        .then(() => dispatch(getInfo({trackLoginEvent: true}))) // Optionally get user info after reset
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        .then(() => dispatch(hideLoginLoading()))
        .catch(err => {
          if (err.code === NO_NETWORK_EXCEPTION) {
            dispatch(
              pushToOfflineQueue({
                promisedAction: onResetPassword,
                params: inputPayload,
              }),
            );
          }
          // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message
          dispatch(hideLoginLoading());
          throw err;
        })
    );
  },
);

export const setEmail = createPromisedAction(SET_EMAIL, ['email']);

export const writeEmail = createPromisedAction(
  WRITE_EMAIL,
  ['email'],
  // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message

  (dispatchMe: any, {email}: {email: string}, dispatch: any) => {
    return setEmailValue(email)
      .then(() => dispatch(setEmail({email})))
      .then(dispatchMe);
  },
);

export const readEmail = createPromisedAction(
  READ_EMAIL,
  [],
  // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message
  (dispatchMe: any, _: any, dispatch: any) => {
    return getEmailValue()
      .then(email => (email ? dispatch(setEmail({email})) : Promise.resolve()))
      .then(dispatchMe);
  },
);
